import { useEffect, useState, useRef } from "react";
import buildVersion from "@/buildVersion.json";

const STORAGE_KEY = "sion_app_version";
const POLL_MS = 30 * 60 * 1000; // 30 minutes

const isDev = process.env.NODE_ENV === "development";
// Allow manual force-on via query param for end-to-end verification in preview
const forceCheck = typeof window !== "undefined" && new URLSearchParams(window.location.search).has("force_update_check");

async function fetchLatestVersion() {
  try {
    const res = await fetch(`/version.json?t=${Date.now()}`, {
      cache: "no-store",
      headers: { "Cache-Control": "no-cache", Pragma: "no-cache" },
    });
    if (!res.ok) return null;
    const data = await res.json();
    return typeof data?.version === "string" ? data.version : null;
  } catch {
    return null; // network errors are non-blocking per spec
  }
}

// Trigger a service-worker update check. Browsers only check sw.js on their own cadence (~24h
// or on navigation), which is too slow for PWA contractors who keep the app foregrounded all
// day. We call this on the same cadence as the version-poll so SW + bundle stay in lockstep.
async function pokeServiceWorkerUpdate() {
  try {
    if (!("serviceWorker" in navigator)) return;
    const reg = await navigator.serviceWorker.getRegistration();
    if (reg) await reg.update();
  } catch { /* non-blocking */ }
}

async function clearAllCachesAndReload() {
  try {
    if ("serviceWorker" in navigator) {
      const regs = await navigator.serviceWorker.getRegistrations();
      await Promise.all(regs.map((r) => r.unregister()));
    }
  } catch { /* ignore */ }
  try {
    if ("caches" in window) {
      const keys = await caches.keys();
      await Promise.all(keys.map((k) => caches.delete(k)));
    }
  } catch { /* ignore */ }
  try { localStorage.removeItem(STORAGE_KEY); } catch { /* ignore */ }
  // iOS Safari ignores reload(true). Navigating to the same path with a cache-busting query
  // forces a fully fresh document fetch — works reliably across iOS Safari, Chrome iOS, Android Chrome, Samsung Internet.
  const url = window.location.origin + window.location.pathname + "?reload=" + Date.now();
  window.location.href = url;
}

export default function UpdateGate({ children }) {
  const [mustUpdate, setMustUpdate] = useState(false);
  const intervalRef = useRef(null);
  const myVersion = buildVersion?.version;

  useEffect(() => {
    // Skip the check entirely in development unless explicitly forced
    if (isDev && !forceCheck) return;

    const runCheck = async () => {
      // Poke the SW first so any newly-deployed sw.js is picked up at the same cadence as the
      // version-poll. Fire-and-forget — we never block the version check on it.
      pokeServiceWorkerUpdate();
      try {
        window.__sion_last_update_check = new Date().toISOString();
      } catch { /* ignore */ }
      const latest = await fetchLatestVersion();
      if (!latest) return; // network failure → non-blocking
      try {
        window.__sion_last_seen_server_version = latest;
      } catch { /* ignore */ }
      if (latest !== myVersion) {
        setMustUpdate(true);
      } else {
        try { localStorage.setItem(STORAGE_KEY, latest); } catch { /* ignore */ }
      }
    };

    runCheck();
    intervalRef.current = setInterval(runCheck, POLL_MS);

    // On mobile the tab is often backgrounded — re-check the moment the user brings it back.
    const onVisibility = () => { if (document.visibilityState === "visible") runCheck(); };
    // `pageshow` covers iOS Safari restoring the page from the back-forward cache, which doesn't
    // always fire `visibilitychange` on home-screen-installed PWAs. Without this, iOS contractors
    // who suspend-and-resume the PWA may miss a version check until the 30-min interval ticks.
    const onPageShow = () => { runCheck(); };
    document.addEventListener("visibilitychange", onVisibility);
    window.addEventListener("focus", onVisibility);
    window.addEventListener("pageshow", onPageShow);

    return () => {
      if (intervalRef.current) clearInterval(intervalRef.current);
      document.removeEventListener("visibilitychange", onVisibility);
      window.removeEventListener("focus", onVisibility);
      window.removeEventListener("pageshow", onPageShow);
    };
  }, [myVersion]);

  if (mustUpdate) {
    return (
      <div
        data-testid="update-gate-overlay"
        style={{
          position: "fixed",
          top: 0,
          left: 0,
          width: "100vw",
          height: "100vh",
          minHeight: "100dvh", // dynamic viewport — accounts for mobile address bar
          zIndex: 99999,       // higher than any nav, header or modal
          background: "#FFFFFF",
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
          padding: 24,
          boxSizing: "border-box",
          overflow: "hidden",
          overscrollBehavior: "none",
          touchAction: "none",
          WebkitTapHighlightColor: "transparent",
          textAlign: "center",
        }}
      >
        <div style={{ width: "100%", maxWidth: 420 }}>
          <img
            src="/sion-logo.png"
            alt="SION"
            onError={(e) => { e.currentTarget.style.display = "none"; }}
            style={{ width: 96, height: "auto", margin: "0 auto 28px", display: "block" }}
          />
          <h1 style={{ fontSize: 22, lineHeight: 1.2, fontWeight: 700, color: "#1C1917", marginBottom: 14 }}>
            Update Required
          </h1>
          <p style={{ fontSize: 14, lineHeight: 1.55, color: "#57534E", marginBottom: 28 }}>
            A new version of SION is available. Please update to continue. This ensures you have the latest fixes and improvements.
          </p>
          <button
            data-testid="update-gate-btn"
            onClick={clearAllCachesAndReload}
            style={{
              background: "#E05C2A",
              color: "#FFFFFF",
              border: "none",
              borderRadius: 12,
              padding: "16px 32px",
              fontSize: 16,
              fontWeight: 600,
              cursor: "pointer",
              width: "100%",
              maxWidth: 320,
              minHeight: 52,
              WebkitAppearance: "none",
              touchAction: "manipulation",
            }}
          >
            Update Now
          </button>
        </div>
      </div>
    );
  }

  return children;
}
